import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { InspectionType, describeInterval } from '../../models/inspection.models';
import { VehicleInspectionService } from '../../services/vehicle-inspection.service';

interface BulkRow {
  vehicleId: number;
  registration: string;
  description: string;
  ticked: boolean;
  nextDueDate: string;
}

/** Add one inspection type to many vehicles at once, each with its own next due date. */
@Component({
    selector: 'app-inspections-bulk',
    templateUrl: './inspections-bulk.component.html',
    standalone: false
})
export class InspectionsBulkComponent implements OnInit {
  types: InspectionType[] = [];
  typeId: number = -1;
  rows: BulkRow[] = [];
  allDate: string = '';
  search: string = '';
  error: string = '';
  result: string = '';
  busy: boolean = false;
  readonly describeInterval = describeInterval;

  constructor(private http: HttpClient, private service: VehicleInspectionService) { }

  ngOnInit(): void {
    this.service.getTypes(true).pipe(take(1)).subscribe((t) => this.types = t);
    // Active vehicles only: trailers are vehicles too.
    this.http.get<any[]>(environment.api.endpoint + 'vehicle?filter=active||eq||1&sort=registration,ASC').pipe(take(1))
      .subscribe((vehicles) => this.rows = vehicles.map((v) => ({
        vehicleId: v.id, registration: v.registration || v.name || ('Vehicle #' + v.id),
        description: [v.make, v.model].filter(Boolean).join(' '), ticked: false, nextDueDate: '',
      })));
  }

  visibleRows(): BulkRow[] {
    const s = this.search.trim().toLowerCase();
    return s ? this.rows.filter((r) => (r.registration + ' ' + r.description).toLowerCase().includes(s)) : this.rows;
  }

  tickedCount(): number {
    return this.rows.filter((r) => r.ticked).length;
  }

  applyDateToTicked(): void {
    this.rows.filter((r) => r.ticked).forEach((r) => r.nextDueDate = this.allDate);
  }

  selectedType(): InspectionType | undefined {
    return this.types.find((t) => t.id === Number(this.typeId));
  }

  save(): void {
    this.error = '';
    this.result = '';
    const ticked = this.rows.filter((r) => r.ticked);
    if (!(this.typeId > 0)) {
      this.error = 'Choose an inspection type.';
      return;
    }
    if (!ticked.length) {
      this.error = 'Tick at least one vehicle.';
      return;
    }
    if (ticked.some((r) => !r.nextDueDate)) {
      this.error = 'Enter a next due date for every ticked vehicle.';
      return;
    }

    this.busy = true;
    this.service.bulkAdd(Number(this.typeId), ticked.map((r) => ({ vehicleId: r.vehicleId, nextDueDate: r.nextDueDate })))
      .pipe(take(1)).subscribe({
        next: (res) => {
          this.busy = false;
          const reg = (id: number) => this.rows.find((r) => r.vehicleId === id)?.registration;
          this.result = `Added to ${res.added.length} vehicle(s).`
            + (res.skipped.length ? ` Already had it: ${res.skipped.map(reg).join(', ')}.` : '');
          this.rows.forEach((r) => { r.ticked = false; r.nextDueDate = ''; });
        },
        error: (e) => {
          this.busy = false;
          this.error = (e && e.error && e.error.message) || 'Something went wrong. Please try again.';
        },
      });
  }
}
